import {
  PURE_BREED_SHARE_SHEET_TEXT_ID,
  THREE_OR_MORE_MIXED_BREED_SHARE_SHEET_TEXT_ID,
  TWO_MIXED_BREED_SHARE_SHEET_TEXT_ID,
} from 'constants/query/shareSheetTextQuery';
import { SocialShareModalContext } from 'contexts/SocialShareModalContext';
import { downloadFile } from 'helpers/fileDownload';
import { getDomain } from 'helpers/getDomain';
import { getSharedImageURL } from 'helpers/getImageURL';
import { useGetBreeds } from 'hooks/useGetBreeds';
import { useGetUserPetProfiles } from 'hooks/useGetUserPetProfiles';
import { observer } from 'mobx-react-lite';
import React, {
  FC,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import toast from 'react-hot-toast';
import { useParams } from 'react-router-dom';

interface SocialShareModalProviderProps {
  children: ReactNode;
}

const SOMETHING_WENT_WRONG = 'Something went wrong, please reload the page and try again';

const processError = (e: unknown, message?: string) => {
  console.error(e);
  const msg = message ?? (e as any).message ?? SOMETHING_WENT_WRONG;
  toast.error(msg);
};

export const SocialShareModalProvider: FC<SocialShareModalProviderProps> = observer(({
  children,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<unknown | null>(null);
  const [currentTemplate, setCurrentTemplate] = useState<string | undefined>();
  const [sharedImageFile, setSharedImageFile] = useState<File>();

  const { testsStore } = useGetUserPetProfiles();
  const { areProfilesLoaded, generatedImage, petProfile } = testsStore;
  const { breedStore } = useGetBreeds();
  const { breedResults, breedResultsLoaded } = breedStore;
  const { testId } = useParams();

  const sharedImages = useMemo(() => {
    if (!generatedImage?.external_media_ids) return {};
    const templates = Object.keys(
      generatedImage.external_media_ids,
    ) as string[];
    setCurrentTemplate(templates[0]);
    return templates.reduce((obj, template) => (
      {
        ...obj,
        [template]: getSharedImageURL(
          generatedImage.external_media_ids[template].media_id,
        ),
      }
    ), {} as { [key: string]: string });
  }, [
    generatedImage,
  ]);

  const isReady = useMemo(
    () => areProfilesLoaded && breedResultsLoaded,
    [areProfilesLoaded, breedResultsLoaded],
  );
  const shareUrl = useMemo(() => {
    const domain = getDomain();
    return `https://petdna.${domain}/share/${testId}`;
  }, [testId]);

  const loadShareData = useCallback(async (imageId?: string) => {
    if (!isReady || !testId || !breedResults || !petProfile) {
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const profileImageId = imageId ?? petProfile?.imageId;
      const image = await testsStore.generateShareImage(testId, {
        breeds: breedResults.breeds,
        imageId: profileImageId,
      });

      const breedCount = image?.message?.breeds.length || 0;
      let sharedSheetTextId = THREE_OR_MORE_MIXED_BREED_SHARE_SHEET_TEXT_ID;

      if (breedCount === 1) {
        sharedSheetTextId = PURE_BREED_SHARE_SHEET_TEXT_ID;
      } else if (breedCount === 2) {
        sharedSheetTextId = TWO_MIXED_BREED_SHARE_SHEET_TEXT_ID;
      }

      await testsStore.getShareSheetText(sharedSheetTextId);
    } catch (e) {
      setError(e);
      processError(e, SOMETHING_WENT_WRONG);
    } finally {
      setIsLoading(false);
    }
  }, [breedResults, isReady, testId, testsStore, petProfile]);

  const openModal = useCallback(async () => {
    setIsOpen(true);
    loadShareData();
    document.body.classList.add('body-no-scroll');
  }, [loadShareData]);

  const closeModal = useCallback(() => {
    setIsOpen(false);
    document.body.classList.remove('body-no-scroll');
  }, []);

  const downloadImage = useCallback(() => {
    if (!currentTemplate) return;
    const sharedImageUrl = sharedImages[currentTemplate];
    if (!sharedImageUrl) return;

    fetch(sharedImageUrl)
      .then((response) => response.blob())
      .then((blob) => {
        const url = window.URL.createObjectURL(blob);
        downloadFile(`PET-DNA-${generatedImage?.pet_name}.png`, url);
      })
      .catch((err) => processError(err, SOMETHING_WENT_WRONG));
  }, [
    generatedImage?.pet_name,
    sharedImages,
    currentTemplate,
  ]);

  useEffect(() => {
    if (!currentTemplate) return;
    const sharedImageUrl = sharedImages[currentTemplate];
    if (!sharedImageUrl) return;

    fetch(sharedImageUrl)
      .then((response) => response.blob())
      .then((blob) => {
        const file = new File(
          [blob],
          `Breed result ${generatedImage?.pet_name}.png`,
          { type: blob.type },
        );
        setSharedImageFile(file);
      })
      .catch((err) => processError(err, SOMETHING_WENT_WRONG));
  }, [
    generatedImage?.pet_name,
    sharedImages,
    currentTemplate,
  ]);

  const contextValue = useMemo(
    () => ({
      isOpen,
      isLoading: isLoading || !isReady,
      shareUrl,
      sharedImages,
      setCurrentTemplate,
      openModal,
      closeModal,
      downloadImage,
      error,
      sharedImageFile,
      loadShareData,
      setIsLoading,
    }),
    [
      isOpen,
      isLoading,
      isReady,
      shareUrl,
      sharedImages,
      setCurrentTemplate,
      openModal,
      closeModal,
      downloadImage,
      error,
      sharedImageFile,
      loadShareData,
      setIsLoading,
    ],
  );

  return (
    <SocialShareModalContext.Provider value={contextValue}>
      {children}
    </SocialShareModalContext.Provider>
  );
});
